import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { Loader2, Sparkles } from "lucide-react";
import { cn } from "@weco/ui";
import { useUpdateAssistantProfile } from "../../../modules/assistant/hooks/useAssistantProfile";
import { useAssistantConfig } from "../useAssistantConfig";
import { useShellStore } from "../../../store";
import { HueSwatches } from "./HueSwatches";

const NAME_IDEAS = ["Pixel", "Mochi", "Nori", "Bolt", "Sprout", "Biscuit"];
const COMPANION_NAME_MAX = 24;

/** First-open gate: has this user already named/onboarded their companion?
 * Reads `onboarding_dismissed` off the RAW assistant-profile query — the
 * resolved config block does NOT carry this field. */
export function shouldShowNaming(
  profile: { onboarding_dismissed?: boolean } | null | undefined,
): boolean {
  return !profile?.onboarding_dismissed;
}

export function NamingStep({ onDone }: { onDone: () => void }) {
  const user = useShellStore((s) => s.user);
  const cfg = useAssistantConfig();
  const updateProfile = useUpdateAssistantProfile();

  const [name, setName] = useState("");
  const [hue, setHue] = useState(cfg.avatar_hue);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const inputId = useId();
  const firstName = user?.name ? user.name.split(" ")[0] : "";

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const pickHue = (nextHue: number) => {
    setHue(nextHue);
    // Optimistic recolour: PATCH immediately, same as the kit's `repaint`.
    updateProfile.mutate({ avatar_hue: nextHue });
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Pick a name first — anything you like.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await updateProfile.mutateAsync({
        assistant_name: trimmed,
        avatar_hue: hue,
        onboarding_dismissed: true,
      });
      onDone();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "That didn't save. Try again?");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-1 flex-col gap-4 overflow-y-auto p-5">
      <div className="rounded-3xl bg-muted/70 p-4 text-sm ring-1 ring-inset ring-border">
        <p className="font-semibold text-foreground">Hi {firstName}! 👋</p>
        <p className="mt-1 text-muted-foreground">
          I&rsquo;m your new office buddy. I can look at your calendar, tasks and leave, and help
          you work out what to do today. Only you can see what we talk about.
        </p>
      </div>

      <div>
        <label htmlFor={inputId} className="text-sm font-medium text-foreground">
          What would you like to call me?
        </label>
        <input
          ref={inputRef}
          id={inputId}
          className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus:ring-1 focus:ring-border"
          value={name}
          maxLength={COMPANION_NAME_MAX}
          onChange={(event) => setName(event.target.value)}
          placeholder="e.g. Pixel"
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${inputId}-error` : undefined}
        />
        <div className="mt-2 flex flex-wrap gap-1.5">
          {NAME_IDEAS.map((idea) => (
            <button
              key={idea}
              type="button"
              className="rounded-full bg-muted px-3 py-1 text-xs font-medium text-foreground ring-1 ring-inset ring-border hover:bg-muted/70"
              onClick={() => setName(idea)}
            >
              {idea}
            </button>
          ))}
        </div>
        {error ? (
          <p id={`${inputId}-error`} className="mt-2 text-sm text-rose-600" role="alert">
            {error}
          </p>
        ) : null}
      </div>

      <div>
        <p className="mb-2 text-sm font-medium text-foreground">And my colour</p>
        <HueSwatches value={hue} onPick={pickHue} />
      </div>

      <button
        type="submit"
        disabled={saving}
        className={cn(
          "mt-auto flex items-center justify-center gap-2 self-stretch rounded-full px-4 py-2.5 text-sm font-semibold text-white transition-opacity",
          "disabled:opacity-60",
        )}
        style={{ background: "linear-gradient(135deg, #3366ff, #5b8cff)" }}
      >
        {saving ? (
          <Loader2 className="size-4 animate-spin" aria-hidden />
        ) : (
          <Sparkles className="size-4" aria-hidden />
        )}
        {name.trim() ? `Nice to meet you, ${name.trim()}!` : "That's me!"}
      </button>
    </form>
  );
}
